import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions} from "../PA.Core/PA.Core.IF.BO";
import {ClassificationControlBOValidations} from "./ClassificationControlBO.Validations";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Classification,
    esWB_MasterData_Set,
    esWB_SalesOrg,
    esWB_SalesArea,
    esWB_Company,
    esWB_ControlPoint_Partition,
    esWB_Classification_Control,
    esWB_Classification_Control_Partition
} from "../PA.Core/PAdbSchema";
import {
    WB_ClassificationRow,
    WB_MasterDataSetRow,
    WB_SalesOrgRow,
    WB_SalesAreaRow,
    WB_CompanyRow,
    WB_ControlPointPartitionRow,
    WB_ClassificationControlRow,
    WB_ClassificationControlPartitionRow,
} from "../PA.Core/PAdbSchemaRows";
import {IwbClassificationControl} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {Role_User_Admin} from "../PA.Core/Security/Roles";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {ControlPointService} from "../PA.MD.Services/ControlPointService";
import {BehaviorSubject} from "rxjs";
import {LESpendBOValidations} from "./LESpendBO.Validations";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

export class ClassificationControlBO implements IBusinessObject {
    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ClassificationControl: WB_ClassificationControlRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Classification Control'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_ClassificationControl = null;

        this.IHavePartitions_init()
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_ClassificationControl(): WB_ClassificationControlRow {
        return this.drWB_ClassificationControl;
    }

    async LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Control Point");
        let olc = self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for new Classification Control.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                let newClassificationControlRow  = <WB_ClassificationControlRow> await this.dbContext.createRecordWithKey(esWB_Classification_Control)
                newClassificationControlRow.setDefaults();
                self.drWB_ClassificationControl = newClassificationControlRow;
                self.dbContext.add(esWB_Classification_Control, newClassificationControlRow);
                self.updateIdentity();

                await self.applyCreateOptions()

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;

                self._OnLoadComplete = undefined;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();
        return olc;
    }

    async applyCreateOptions() {
        const self = this
        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_ClassificationControl.cif.CONTROL_POINT_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {

                self.WB_ClassificationControl.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_ClassificationControl.cif.WB_SALESORG_ID = wbSalesOrg
            }
        }

        // Set Sales Area
        let wbSalesArea = self.CreateOptions?.wbSalesAreaId || self.LoadOptions?.wbSalesAreaId
        if (_.isNumber(wbSalesArea)) {
            let salesArea: WB_SalesAreaRow = <any>await self.dbContext.find(esWB_SalesArea, wbSalesArea)
            if (salesArea) {
                self.WB_ClassificationControl.cif.WB_SALESAREA_ID = wbSalesArea
            }
        }

        if (wbSalesArea>0)
            self.WB_ClassificationControl.cif.ENTERPRISE_POLICY="SalesArea"
        else if (wbSalesOrg>0)
            self.WB_ClassificationControl.cif.ENTERPRISE_POLICY="SalesOrg"
        else
            self.WB_ClassificationControl.cif.ENTERPRISE_POLICY="MD_SET"

    }
    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Classification Control already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for Classification Control.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = ClassificationControlBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = ClassificationControlBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aClassificationControlRow: any = recordResolver.get(esWB_Classification_Control, anId);

                if (_.isEmpty(aClassificationControlRow)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }

                self.drWB_ClassificationControl = aClassificationControlRow;
                self.updateIdentity();

                if (_.isNumber(self.drWB_ClassificationControl.cif.WB_MDSET_ID) && self.drWB_ClassificationControl.cif.WB_MDSET_ID > 0) {
                    let mruService = new MDUsageService()
                    await mruService.saveMDSetUsage(self.drWB_ClassificationControl.cif.WB_MDSET_ID)
                }

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    private updateIdentity(): void {

        const {
            WB_CLASSIFICATION_CONTROL_ID,
            CONTROL_POINT_ID,
            CONTROL_POINT_NAME
        } = this.WB_ClassificationControl.cif;
        this.Identity.setAll(WB_CLASSIFICATION_CONTROL_ID, CONTROL_POINT_ID, CONTROL_POINT_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_ClassificationControl, 'WB_CLASSIFICATION_CONTROL_ID', 'CONTROL_POINT_ID', 'CONTROL_POINT_NAME');
        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId(Role_User_Admin)

        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_ClassificationControl, undefined, 'CONTROL_POINT_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_ClassificationControl, undefined, undefined)

    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_ClassificationControl.cif.CONTROL_POINT_NAME + '(' + self.WB_ClassificationControl.cif.CONTROL_POINT_ID + ')'
        )

        self.ValidateOption = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Customer Type.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await ClassificationControlBOValidations.ValidateFields(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const wbClassificationControl = <IwbClassificationControl>self.drWB_ClassificationControl;
        this.SaveProgressProvider.start(
            'Saving ' + wbClassificationControl.CONTROL_POINT_NAME + '(' + wbClassificationControl.CONTROL_POINT_ID + ')'
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Classification Control.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if(!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            await saveFunction(esWB_Classification_Control, self.drWB_ClassificationControl, {})
            await saveFunction(esWB_Classification_Control_Partition, self.drWB_ClassificationControl.WB_ClassificationControlPartitionRows, {})

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {

        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const wbClassificationControl = <IwbClassificationControl>this.drWB_ClassificationControl;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            'Deleting ' + wbClassificationControl.CONTROL_POINT_NAME + '(' + wbClassificationControl.CONTROL_POINT_ID + ')'
        );

        try {
            self.drWB_ClassificationControl.markDeleted();
            _.forEach(self.WB_ClassificationControl.WB_ClassificationControlPartitionRows, (a) => {
                a.markDeleted()
            })

            if (bSaveNow)
                await self.SaveUsing(false, {bSkipValidations: true})

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }
        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_ClassificationControl, opts)
    }


    //</editor-fold>

    //<editor-fold desc = "Partition and Item Operations">

    private _PartitionsChanged: BehaviorSubject<ClassificationControlBO>

    private IHavePartitions_init(): void {
        // Create local BehaviorSubjects
        this._PartitionsChanged = new BehaviorSubject<ClassificationControlBO>(undefined)
    }


    get PartitionsChanged(): BehaviorSubject<ClassificationControlBO> {
        return this._PartitionsChanged
    }


    async addPartition(wbPartitionId?: number): Promise<WB_ClassificationControlPartitionRow> {
        const self = this

        let ccPartition
            = <WB_ClassificationControlPartitionRow>await self.dbContext.createRecordWithKey(esWB_Classification_Control_Partition)
        ccPartition.setDefaults()

        ccPartition.cif.WB_CLASSIFICATION_CONTROL_ID = self.drWB_ClassificationControl.cif.WB_CLASSIFICATION_CONTROL_ID
        if (_.isNumber(wbPartitionId))
            ccPartition.cif.WB_PARTITION_ID = wbPartitionId
        ccPartition.cif.SORT_ORDER = self.WB_ClassificationControl.WB_ClassificationControlPartitionRows.length
        self.dbContext.dctKeyGenSpecsByEntitySet

        self.dbContext.add(esWB_Classification_Control_Partition, ccPartition)

        let DefaultRow = _.first(_.filter(self.WB_ClassificationControl.WB_ClassificationControlPartitionRows, aRow => aRow.isActive && aRow.cif.WB_PARTITION_ID == -1))
        if (!_.isEmpty(DefaultRow))
            DefaultRow.cif.SORT_ORDER = self.WB_ClassificationControl.WB_ClassificationControlPartitionRows.length
        self.PartitionsChanged.next(this)

        return ccPartition
    }

    async removePartition(wbClassificationControlPRId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_ClassificationControl.WB_ClassificationControlPartitionRows, (aRow) => {
            if (!aRow.isActive)
                return

            if (aRow.cif.WB_CLASSIFICATION_CONTROLPR_ID == wbClassificationControlPRId) {
                aRow.markDeleted()
            }
        })

        self.PartitionsChanged.next(this)
    }


    //</editor-fold>


    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const createOptions: CreateOptions = {
            standardConditions: {
                [esWB_Classification_Control]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Classification_Control_Partition]: {"DELETED_IND": {"notIn": ['1']}},
            },
            dctIncludeBelongsToNamedRelations: { }
        };

        return new ESRecordResolver(dbContext, createOptions);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Classification_Control, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(ClassificationControlBO, "ClassificationControlBO", "ClassificationControlBO")
